import rawModel from './v1ProModel.generated.json';
import type { Board, Difficulty, PieceType } from './types';
import { INITIAL_NON_KING_MATERIAL, MATERIAL } from './chessValues';

const PIECE_TYPES: PieceType[] = ['K', 'A', 'E', 'H', 'R', 'C', 'P'];
const PIECE_INDEX = new Map(PIECE_TYPES.map((type, index) => [type, index]));

interface V1ProModel {
  schemaVersion: number;
  status: 'trained' | 'untrained';
  generatedAt: string;
  method: string;
  sampleCount: number;
  validationCount: number;
  validationRmse: number;
  baselineRmse: number;
  baselineScale: number;
  blend?: number;
  pieceSquareMid: number[][];
  pieceSquareEnd: number[][];
}

export const V1_PRO_MODEL = rawModel as V1ProModel;
const V1_PRO_LEVELS = new Set<Difficulty>(['trader', 'cannonhorse', 'gambiteer', 'pawn']);

export function usesV1Pro(difficulty: Difficulty): boolean {
  return V1_PRO_LEVELS.has(difficulty) && V1_PRO_MODEL.status === 'trained';
}

export function evaluateWithV1Pro(board: Board, baselineRedScore: number, blend = V1_PRO_MODEL.blend ?? 0.25): number {
  if (V1_PRO_MODEL.status !== 'trained') return baselineRedScore;
  let remainingMaterial = 0;
  for (const piece of board) if (piece && piece.type !== 'K') remainingMaterial += MATERIAL[piece.type];
  const endgame = Math.max(0, Math.min(1, 1 - remainingMaterial / INITIAL_NON_KING_MATERIAL));
  let correction = 0;
  for (let square = 0; square < board.length; square += 1) {
    const piece = board[square];
    if (!piece) continue;
    const pieceIndex = PIECE_INDEX.get(piece.type);
    if (pieceIndex === undefined) continue;
    const canonicalSquare = piece.side === 'red' ? square : 89 - square;
    const sign = piece.side === 'red' ? 1 : -1;
    const mid = V1_PRO_MODEL.pieceSquareMid[pieceIndex]?.[canonicalSquare] ?? 0;
    const end = V1_PRO_MODEL.pieceSquareEnd[pieceIndex]?.[canonicalSquare] ?? 0;
    correction += sign * (mid * (1 - endgame) + end * endgame);
  }
  const learnedScore = baselineRedScore * V1_PRO_MODEL.baselineScale + correction;
  return Math.round(baselineRedScore + (learnedScore - baselineRedScore) * Math.max(0, Math.min(1, blend)));
}
